import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';

interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => ReactNode;
  width?: string;
  sortable?: boolean;
  sortKey?: string; // Optional different key to sort by (e.g., for computed columns)
}

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (item: T) => void;
  sortConfig?: SortConfig | null;
  onSort?: (key: string) => void;
  hidePagination?: boolean;
}

export function DataTable<T extends object>({
  columns,
  data,
  loading,
  page = 1,
  totalPages = 1,
  onPageChange,
  onRowClick,
  sortConfig,
  onSort,
  hidePagination = false,
}: DataTableProps<T>) {
  const handleHeaderClick = (col: Column<T>) => {
    if (col.sortable && onSort) {
      onSort(col.sortKey || col.key);
    }
  };

  const getSortIcon = (col: Column<T>) => {
    const sortKey = col.sortKey || col.key;
    if (!sortConfig || sortConfig.key !== sortKey) {
      return null;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp size={14} className="inline ml-1" aria-hidden="true" />
    ) : (
      <ChevronDown size={14} className="inline ml-1" aria-hidden="true" />
    );
  };

  const getAriaSortValue = (col: Column<T>): 'ascending' | 'descending' | 'none' | undefined => {
    if (!col.sortable) return undefined;
    const sortKey = col.sortKey || col.key;
    if (!sortConfig || sortConfig.key !== sortKey) return 'none';
    return sortConfig.direction === 'asc' ? 'ascending' : 'descending';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  style={{ width: col.width }}
                  aria-sort={getAriaSortValue(col)}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleHeaderClick(col)}
                      className="w-full text-left hover:bg-gray-100 -mx-2 px-2 py-1 rounded select-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-label={`Sort by ${col.header}, currently ${getAriaSortValue(col) === 'none' ? 'unsorted' : getAriaSortValue(col)}`}
                    >
                      {col.header}
                      {getSortIcon(col)}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-12 text-center text-gray-500"
                  role="status"
                  aria-live="polite"
                >
                  Loading...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-12 text-center text-gray-500"
                  role="status"
                >
                  No data available
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr
                  key={index}
                  className={`${
                    onRowClick
                      ? 'cursor-pointer hover:bg-gray-50'
                      : ''
                  }`}
                  onClick={() => onRowClick?.(item)}
                  onKeyDown={(e) => {
                    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onRowClick(item);
                    }
                  }}
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'button' : undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                    >
                      {col.render
                        ? col.render(item)
                        : ((item as Record<string, unknown>)[col.key] as ReactNode)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!hidePagination && totalPages > 1 && (
        <nav
          className="px-6 py-4 border-t border-gray-200 flex items-center justify-between"
          aria-label="Pagination"
        >
          <p className="text-sm text-gray-500" aria-live="polite">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-1" role="group" aria-label="Page navigation">
            {/* Previous button */}
            <button
              onClick={() => onPageChange?.(page - 1)}
              disabled={page <= 1}
              aria-label="Previous page"
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>

            {/* Page numbers */}
            {(() => {
              const pages: (number | string)[] = [];
              const showPages = 5; // Max pages to show

              if (totalPages <= showPages + 2) {
                // Show all pages if not many
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                // Always show first page
                pages.push(1);

                // Calculate range around current page
                let start = Math.max(2, page - 1);
                let end = Math.min(totalPages - 1, page + 1);

                // Adjust if at edges
                if (page <= 3) {
                  end = Math.min(showPages, totalPages - 1);
                } else if (page >= totalPages - 2) {
                  start = Math.max(2, totalPages - showPages + 1);
                }

                // Add ellipsis before if needed
                if (start > 2) pages.push('...');

                // Add middle pages
                for (let i = start; i <= end; i++) pages.push(i);

                // Add ellipsis after if needed
                if (end < totalPages - 1) pages.push('...');

                // Always show last page
                pages.push(totalPages);
              }

              return pages.map((p, idx) => (
                typeof p === 'string' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-gray-400" aria-hidden="true">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => onPageChange?.(p)}
                    aria-label={`Page ${p}`}
                    aria-current={p === page ? 'page' : undefined}
                    className={`min-w-[32px] h-8 rounded-lg border text-sm font-medium transition-colors ${
                      p === page
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              ));
            })()}

            {/* Next button */}
            <button
              onClick={() => onPageChange?.(page + 1)}
              disabled={page >= totalPages}
              aria-label="Next page"
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
